import React, { useEffect, useRef, useState } from 'react';

import { SureYesNoButton } from '../../../ClickOnceButton';
import { CheckinSummaryNodes } from '../../../components/CheckinSummaryNodes';
import { DeviceStatusBoxes } from '../../../components/DeviceStatusBoxes';
import { useAuth } from '../../../context/AuthContext';
import { useDevices } from '../../../context/DeviceContext';
import { useGeofenceAreas } from '../../../context/GeofenceAreaContext';
import { useMap } from '../../../context/MapContext';
import { produceDeviceMarkers } from '../../../DeviceMarkerFactory';
import { SideWays } from '../../../Sideways';
import { smallButtonStyle, smallTextStyle } from '../../../styles/constants';
import { TimeSpace } from '../../../TimeSpace';
import type { AreaShape, Device, LatLng, TracePoint } from '../../../types';
import { adminAPI, checkinAPI, deviceAPI, geofenceAreaConfigAPI } from '../../../utils/apiClient';
import { showAsAlarm, showAsTrace, showAsActive } from '../../../utils/deviceUtils';
import { useRedrawCounter } from '../../../utils/hooks';
import logger from '../../../utils/logger';
import CameraLockEditor from '../CameraLock/CameraLockEditor';
import AreaEditor from '../GeofenceAreas/AreaEditor';
import DrawingLayer from '../GeofenceAreas/DrawingLayer';
import GeofenceAreas from '../GeofenceAreas/GeofenceAreas';
import PositionPickerLayer from '../GeofenceAreas/PositionPickerLayer';

import { MyAccountPanel } from './MyAccountPanel';
import { PushSubscriptionButton } from './PushSubscriptionButton';

// Device categories whose point markers can be replaced by a geofence area overlay.
// Used to hold these markers until the area configs (and thus the suppression set) load.
const GEOFENCEABLE_CATEGORIES: readonly string[] = ['lf_beacon', 'lf_checkin'];

/**
 * Extended device for map display with callback handlers
 */
interface MapDevice extends Device {
  lat: number;
  lng: number;
  isChecker: boolean;
  currentCheckpoint: string | null;
  onSelect?: () => void;
  onAck?: () => void;
  onDis?: () => void;
  onBeginTracking?: () => void;
  onDeactivateTracking?: () => void;
  onCheckin?: () => void;
  onUncheck?: () => void;
  onSetCurrentCheckpoint?: (checkpointId: string | null) => void;
  onClearAllCheckins?: (checkpointId: string) => void;
}

/**
 * Props for the Map component
 */
export interface MapProps {
  /** Map projection type ("timespace" or "sideways") */
  projection?: string;
  /** Map center coordinates [lat, lng] */
  center?: LatLng;
  /** Map zoom level */
  zoom?: number;
  /** Function to update zoom level */
  setZoom?: (zoom: number) => void;
  /** Whether waiting for user geolocation */
  waitForPos?: boolean;
  /** Logout handler function */
  onLogout: () => void;
  /** Device IDs that have a drawn area_shape — suppress point marker for these */
  devicesWithAreaShape?: Set<string>;
  /** True once geofence area configs have loaded; until then beacon/checkin markers are held back */
  areaConfigsLoaded?: boolean;
  /** Incremented each time the admin requests opening the geofence area editor */
  openAreaEditorRequest?: number;
}

/**
 * Map Component - Interactive device map display
 *
 * Renders devices on an interactive map with filtering capabilities.
 * Supports two projection modes: "timespace" and "sideways".
 * Integrates with DeviceContext for device data and filtering state.
 *
 * Features:
 * - Real-time device positioning
 * - Device selection and interaction
 * - Multiple projection modes (TimeSpace, SideWays)
 * - Device filtering by various criteria
 * - User authentication display
 * - Admin controls for device management
 */
export const Map: React.FC<MapProps> = ({
  projection: propProjection,
  center: propCenter,
  zoom: propZoom,
  setZoom: propSetZoom,
  waitForPos = false,
  onLogout,
  devicesWithAreaShape,
  areaConfigsLoaded = true,
  openAreaEditorRequest = 0,
}) => {
  const {
    user,
    account,
    isAdmin,
    isChecker,
    isSuperAdmin,
    defaultCenter,
    cameraLock,
    cameraLockOverride,
    setCameraLockOverride,
  } = useAuth();

  // SuperAdmins are never locked, and a locked user may temporarily override the lock
  // for the session. Both collapse the effective lock to null (no enforcement).
  const effectiveCameraLock = isSuperAdmin || cameraLockOverride ? null : cameraLock;
  // A non-SuperAdmin user is actually subject to a lock when one is enabled with a shape.
  const userIsLocked = !isSuperAdmin && !!cameraLock?.enabled && !!cameraLock?.shape;
  const {
    devices,
    filteredDevices,
    selectedDevice,
    selectDevice,
    currentCheckpoint,
    updateCurrentCheckpoint,
    searchQuery,
    fetchDevices,
    showActiveIndicator,
    showBatchTrackingButtons,
    showTraceButton,
    showCheckinSummary,
    showDeviceStatusBoxes,
  } = useDevices();
  const {
    projection,
    center,
    zoom,
    setZoom,
    openTraceView,
    fitBoundsRequest,
    clearFitBoundsRequest,
  } = useMap();
  const { refresh: refreshAreaConfigs } = useGeofenceAreas();

  // Local state for map-specific functionality
  const [error, setError] = useState<string | null>(null);
  const [showMyAccount, setShowMyAccount] = useState(false);
  const [showAccuracy, setShowAccuracy] = useState(false);
  const [loadingTrace, setLoadingTrace] = useState(false);

  // Geofence area editor state
  const [areaEditorOpen, setAreaEditorOpen] = useState(false);
  const [drawMode, setDrawMode] = useState<'circle' | 'rectangle' | 'polygon' | null>(null);
  const [pendingShape, setPendingShape] = useState<AreaShape | null>(null);
  const [pendingPosition, setPendingPosition] = useState<[number, number] | null>(null);
  const [awaitingPosition, setAwaitingPosition] = useState(false);

  // Camera lock editor state (SuperAdmin). While the editor is open the active lock
  // is suspended so the admin can freely pan/zoom to draw a new restriction area.
  const [cameraLockEditorOpen, setCameraLockEditorOpen] = useState(false);
  const [cameraLockDrawing, setCameraLockDrawing] = useState(false);
  const [cameraLockPendingShape, setCameraLockPendingShape] = useState<AreaShape | null>(null);
  const [cameraLockDrawZoom, setCameraLockDrawZoom] = useState<number | null>(null);

  // One-shot fly-to request passed down to TimeSpace when an alarm first appears.
  // A new object reference triggers the effect; null means no pending request.
  const [flyToTarget, setFlyToTarget] = useState<{ positions: LatLng[]; force?: boolean } | null>(
    null
  );

  // Tracks last known device position across renders so the map stays put when devices disappear
  const lastDevicePosRef = useRef<LatLng | null>(null);

  // Tracks alarm device IDs seen in the previous render to detect new arrivals.
  const prevAlarmIdsRef = useRef<Set<string>>(new Set());

  // Auto-refresh for real-time updates - optimized with custom hook
  const redraw = useRedrawCounter(30000);

  /**
   * Process devices for map display
   * Applies filtering, adds map-specific properties, and prepares for rendering
   */
  const processDevicesForMap = (): MapDevice[] => {
    const processedDevices: MapDevice[] = [];

    filteredDevices.forEach((device) => {
      // Until geofence area configs have loaded, hold back beacon/checkin point markers.
      // The suppression set below is empty on first paint, so rendering them now would
      // flash geofenced infrastructure as scattered points before correcting to the area
      // overlay (the fresh-login flicker). Holding briefly is preferable to showing wrong.
      if (!areaConfigsLoaded && GEOFENCEABLE_CATEGORIES.includes(device.deviceCategory ?? '')) {
        return;
      }

      // Infrastructure devices with a drawn area_shape use the polygon as their
      // visual representation — suppress the point marker to avoid duplication.
      if (devicesWithAreaShape?.has(device.id)) return;

      // Ensure device has valid coordinates
      if (!Array.isArray(device.latlng) || device.latlng.length < 2) {
        return; // Skip devices without coordinates
      }

      if (!Number.isFinite(device.latlng[0]) || !Number.isFinite(device.latlng[1])) {
        return; // Skip devices with invalid coordinates
      }

      // Create map-ready device object
      const mapDevice: MapDevice = {
        ...device,
        lat: device.latlng[0],
        lng: device.latlng[1],
        positionTime: device.timestamp || device.positionTime, // Ensure positionTime exists for TimeSpace
        checkpoint: !!device.checkpoint,
        isChecker: isChecker,
        currentCheckpoint: currentCheckpoint,
        // onSelect drives the "Trace" button in the timespace popup
        // (DeviceMarkerFactory). Gate it on the server setting so the button is
        // hidden when "Show Trace button in device popup" is off — the Sideways
        // projection gates its own Trace button on the same flag.
        ...(showTraceButton && {
          onSelect: () => {
            selectDevice(device.id);
            loadDeviceTrace(device.id);
          },
        }),
        ...(showBatchTrackingButtons && {
          onBeginTracking: () => handleBeginTracking(device.id),
          onDeactivateTracking: () => handleDeactivateTracking(device.id),
        }),
        onCheckin: () => handleCheckin(device.id),
        onUncheck: () => handleUncheck(device.id),
        onSetCurrentCheckpoint: (checkpointId: string | null) =>
          handleSetCurrentCheckpoint(checkpointId),
        onClearAllCheckins: (checkpointId: string) => handleClearAllCheckins(checkpointId),
      };

      // Add alarm acknowledgment handler for admin users
      if (device.alarmTime && device.canAcknowledge && !device.alarmAcknowledge && isAdmin) {
        mapDevice.onAck = () => {
          logger.debug(`Acknowledging alarm for device: ${device.id}`);
        };
      }

      // Add alarm disable handler for admin users — backend resolves the VSM field and sends downlink
      if (device.alarmTime && isAdmin) {
        mapDevice.onDis = async () => {
          try {
            await adminAPI.rejectAlarm(device.id);
            await fetchDevices();
          } catch (err) {
            logger.error({ err }, `Failed to reject alarm for device: ${device.id}`);
          }
        };
      }

      // Only show devices that are actively relevant on the map:
      // checkpoint infrastructure, active alarm, active trace, active device (when enabled),
      // recently checked in, or beacon infrastructure nodes (always shown at fixed position).
      // showActiveIndicator controls whether deviceActive=1 devices are shown at all.
      const isVisible =
        device.checkpoint ||
        showAsAlarm(device) ||
        showAsTrace(device) ||
        (showActiveIndicator && showAsActive(device)) ||
        device.checkinTime != null ||
        device.deviceCategory === 'lf_beacon';
      if (!isVisible) return;

      processedDevices.push(mapDevice);
    });

    return processedDevices;
  };

  const mapSeries = processDevicesForMap();
  const selectedDeviceOnly = selectedDevice
    ? mapSeries.filter((device) => device.id === selectedDevice)
    : mapSeries;

  // Debug logging
  logger.debug(
    `Map projection: ${projection}, devices: ${mapSeries.length}, selectedDevice: ${selectedDevice}, showing: ${selectedDeviceOnly.length}`
  );

  // Determine map center — priority: last device > account default > geolocation/explicit > last known > world view
  const lastInSeries: LatLng | null =
    selectedDeviceOnly.length > 0
      ? (selectedDeviceOnly[selectedDeviceOnly.length - 1].latlng as LatLng)
      : mapSeries.length > 0
        ? (mapSeries[mapSeries.length - 1].latlng as LatLng)
        : null;
  if (lastInSeries) lastDevicePosRef.current = lastInSeries;

  const mapCenter: LatLng = lastInSeries ??
    (defaultCenter ? ([defaultCenter.lat, defaultCenter.lng] as LatLng) : null) ??
    center ??
    lastDevicePosRef.current ?? [20, 0];

  // Use defaultZoom for the initial view when no devices are visible; otherwise keep current zoom.
  const effectiveZoom =
    lastInSeries === null && defaultCenter?.zoom != null ? defaultCenter.zoom : zoom;

  // Detect newly appeared alarm devices and trigger a one-shot fly-to covering all alarms.
  // Collects ALL currently alarming positions so the map can show them together in one move.
  useEffect(() => {
    const currentAlarmDevices = mapSeries.filter((d) => showAsAlarm(d));
    const currentAlarmIds = new Set(currentAlarmDevices.map((d) => d.id));

    const hasNewAlarm = currentAlarmDevices.some((d) => !prevAlarmIdsRef.current.has(d.id));

    if (hasNewAlarm) {
      const positions = currentAlarmDevices.map((d) => [d.lat, d.lng] as LatLng);
      logger.debug(`Alarm device(s) appeared — flying to ${positions.length} position(s)`);
      setFlyToTarget({ positions });
    }

    prevAlarmIdsRef.current = currentAlarmIds;
  }, [mapSeries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the area editor whenever the admin panel increments this counter.
  useEffect(() => {
    if (openAreaEditorRequest > 0) setAreaEditorOpen(true);
  }, [openAreaEditorRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle checkpoint selection
   */
  const handleSetCurrentCheckpoint = (checkpointId: string | null): void => {
    logger.debug(`Setting current checkpoint: ${checkpointId}`);
    updateCurrentCheckpoint(checkpointId);
  };

  /**
   * Load trace data for a device and open trace view
   */
  const loadDeviceTrace = async (deviceId: string): Promise<void> => {
    if (!deviceId || loadingTrace) return;

    logger.debug(`Loading trace for device: ${deviceId}`);
    setLoadingTrace(true);

    try {
      const data = await deviceAPI.getTrace(deviceId);
      handleTraceDataLoaded(data);
    } catch (err) {
      handleTraceDataError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  /**
   * Handle "Show Trace" from Sideways popup
   */
  const handleShowTrace = (deviceId: string): void => {
    selectDevice(deviceId);
    loadDeviceTrace(deviceId);
  };

  /**
   * Handle "Begin Tracking" - starts real-time tracking of device
   */
  const handleBeginTracking = async (deviceId: string): Promise<void> => {
    try {
      const result = await deviceAPI.downlink(deviceId, 2, '9100000001');
      logger.debug(`Started tracking for device ${deviceId}:`, result);
    } catch (err) {
      logger.error(`Failed to start tracking for device ${deviceId}:`, err);
    }
  };

  /**
   * Handle "Deactivate Tracking" - stops real-time tracking of device
   */
  const handleDeactivateTracking = async (deviceId: string): Promise<void> => {
    try {
      const result = await deviceAPI.downlink(deviceId, 2, '9100000000');
      logger.debug(`Stopped tracking for device ${deviceId}:`, result);
    } catch (err) {
      logger.error(`Failed to stop tracking for device ${deviceId}:`, err);
    }
  };

  /**
   * Handle "Check in" - check in device at current checkpoint
   */
  const handleCheckin = async (deviceId: string): Promise<void> => {
    if (!currentCheckpoint) {
      logger.error('No current checkpoint set for check-in');
      return;
    }

    try {
      const result = await deviceAPI.checkin(deviceId, currentCheckpoint);
      logger.debug(`Checked in device ${deviceId} at checkpoint ${currentCheckpoint}:`, result);
      fetchDevices();
    } catch (err) {
      logger.error(`Failed to check in device ${deviceId}:`, err);
    }
  };

  /**
   * Handle "Uncheck" - remove check-in for device
   */
  const handleUncheck = async (deviceId: string): Promise<void> => {
    try {
      const result = await deviceAPI.uncheck(deviceId);
      logger.debug(`Unchecked device ${deviceId}:`, result);
      fetchDevices();
    } catch (err) {
      logger.error(`Failed to uncheck device ${deviceId}:`, err);
    }
  };

  /**
   * Handle "Clear all Checkins" - clear all check-ins at a checkpoint
   */
  const handleClearAllCheckins = async (checkpointId: string): Promise<void> => {
    try {
      const result = await deviceAPI.clearCheckins(checkpointId);
      logger.debug(`Cleared all check-ins at checkpoint ${checkpointId}:`, result);
      fetchDevices();
    } catch (err) {
      logger.error(`Failed to clear check-ins at checkpoint ${checkpointId}:`, err);
    }
  };

  /**
   * Handle successful trace data load
   */
  const handleTraceDataLoaded = (data: unknown): void => {
    const result = data as TracePoint[] | { solutions?: TracePoint[] };
    setLoadingTrace(false);

    if (Array.isArray(result) && result.length > 0) {
      logger.debug(`Loaded ${result.length} trace points for device ${selectedDevice}`);
      openTraceView(selectedDevice || '', result);
    } else if (result && 'solutions' in result && result.solutions) {
      logger.debug(`Loaded ${result.solutions.length} trace points for device ${selectedDevice}`);
      openTraceView(selectedDevice || '', result.solutions);
    } else {
      logger.debug('No trace data found for device');
      openTraceView(selectedDevice || '', []);
    }
  };

  const handleAreaSave = async (
    data: import('../GeofenceAreas/AreaEditor').SaveAreaData
  ): Promise<void> => {
    if (data.id != null) {
      await geofenceAreaConfigAPI.update(data.id, {
        area_name: data.area_name,
        area_shape: data.area_shape,
        position_mode: data.position_mode,
        assignments: data.assignments,
      });
    } else {
      await geofenceAreaConfigAPI.create({
        area_name: data.area_name,
        area_shape: data.area_shape,
        position_mode: data.position_mode,
        assignments: data.assignments,
      });
    }
    refreshAreaConfigs();
    setPendingShape(null);
    setDrawMode(null);
  };

  /**
   * Handle trace data loading error
   */
  const handleTraceDataError = (err: Error | { error: string }): void => {
    setLoadingTrace(false);

    let errorMessage = 'Unknown error';
    if (err instanceof Error) {
      errorMessage = err.message;
    } else if (err && typeof err === 'object' && 'error' in err) {
      errorMessage = err.error;
    }

    setError(`Failed to load trace data: ${errorMessage}`);
    logger.error('Trace loading error:', err);
  };

  return (
    <div>
      {/* Main map container */}
      <div
        style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100vw',
          height: '100vh',
          zIndex: 1,
        }}
      >
        {/* TimeSpace projection */}
        {projection === 'timespace' && (
          <TimeSpace
            width="100vw"
            height="100vh"
            top="0"
            series={selectedDeviceOnly}
            center={mapCenter}
            redraw={redraw}
            maxAge={14 * 24 * 3600}
            selected={selectedDevice}
            zoom={effectiveZoom}
            setZoom={setZoom}
            markerFactory={produceDeviceMarkers}
            waitForPos={waitForPos}
            showAccuracy={showAccuracy}
            flyToTarget={flyToTarget}
            onFlyConsumed={() => setFlyToTarget(null)}
            fitBoundsRequest={fitBoundsRequest}
            onFitBoundsConsumed={clearFitBoundsRequest}
            cameraLock={cameraLockEditorOpen ? null : effectiveCameraLock}
          >
            <GeofenceAreas />
            {drawMode && (
              <DrawingLayer
                drawMode={drawMode}
                onShapeComplete={(shape) => {
                  setPendingShape(shape);
                  setDrawMode(null);
                }}
              />
            )}
            {cameraLockDrawing && (
              <DrawingLayer
                drawMode="rectangle"
                onShapeComplete={(shape) => {
                  setCameraLockPendingShape(shape);
                  setCameraLockDrawZoom(zoom);
                  setCameraLockDrawing(false);
                }}
              />
            )}
            {awaitingPosition && (
              <PositionPickerLayer onPick={(latlng) => setPendingPosition(latlng)} />
            )}
          </TimeSpace>
        )}

        {/* SideWays projection */}
        {projection === 'sideways' && (
          <SideWays
            width="100vw"
            height="100vh"
            top="0"
            series={selectedDeviceOnly}
            isChecker={isChecker}
            isAdmin={isAdmin}
            showTraceButton={showTraceButton}
            setCurrentCheckpoint={handleSetCurrentCheckpoint}
            currentCheckpoint={currentCheckpoint}
            setSelectedId={handleShowTrace}
            redraw={redraw}
            maxAge={14 * 24 * 3600}
            selected={selectedDevice}
            setSelected={selectDevice}
            zoom={zoom}
            setZoom={setZoom}
            markerFactory={produceDeviceMarkers}
            waitForPos={waitForPos}
            showAccuracy={showAccuracy}
          />
        )}

        {/* Top-centre strip: checkin summaries and the device status count boxes sit
            side by side, centred like the summaries always were. The wrapper itself is
            click-through; each child re-enables pointer events. */}
        {(showCheckinSummary || (projection === 'timespace' && showDeviceStatusBoxes)) && (
          <div
            style={{
              position: 'fixed',
              top: 'max(8px, 1vmin)',
              left: '50%',
              transform: 'translateX(-50%)',
              // Above TimeSpace's empty-state overlay (zIndex 1000) so the popout
              // device list opens on top of the "No devices in alarm." message.
              zIndex: 1100,
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              pointerEvents: 'none',
            }}
          >
            {showCheckinSummary && (
              <div style={{ pointerEvents: 'auto' }}>
                <CheckinSummaryNodes
                  inline
                  series={mapSeries}
                  isChecker={isChecker}
                  isAdmin={isAdmin}
                />
              </div>
            )}
            {projection === 'timespace' && showDeviceStatusBoxes && (
              <div style={{ pointerEvents: 'auto' }}>
                <DeviceStatusBoxes
                  onDeviceClick={(deviceId, latlng) => {
                    selectDevice(deviceId);
                    // force: an explicit click must center even if the position is already in view.
                    setFlyToTarget({ positions: [latlng], force: true });
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* No devices message */}
        {projection === 'timespace' && mapSeries.length === 0 && (
          <div
            style={{
              ...smallTextStyle,
              position: 'fixed',
              top: '40vh',
              left: '40vw',
            }}
          >
            {devices.length} available - {devices.length - filteredDevices.length} filtered out.
            {searchQuery && ` Search: "${searchQuery}"`}
          </div>
        )}
      </div>

      {/* User info and logout button — position: fixed so it stays in viewport and cannot scroll away on iPad */}
      <div
        style={{
          position: 'fixed',
          top: '2vmin',
          right: '10vmin',
          fontSize: 'max(12px, 2vmin)',
          color: '#444',
          textAlign: 'right',
          zIndex: 1000,
        }}
      >
        <button
          onClick={() => setShowMyAccount(true)}
          style={{ ...smallButtonStyle, display: 'block', width: '100%', textAlign: 'center' }}
        >
          {user}
        </button>
        <SureYesNoButton onClick={onLogout} text="Log out" />
      </div>

      {showMyAccount && <MyAccountPanel onClose={() => setShowMyAccount(false)} />}

      {/* Accuracy toggle button (for debugging/admin) — hidden in sideways view */}
      {isAdmin && projection !== 'sideways' && (
        <div>
          <div
            style={{
              position: 'fixed',
              bottom: '8vmin',
              right: '7vmin',
              borderRadius: '2vmin',
              height: '4vmin',
              width: '4vmin',
              background: showAccuracy ? '#4CAF50c' : '#cccc',
              cursor: 'pointer',
              zIndex: 1000,
            }}
            onClick={() => setShowAccuracy(!showAccuracy)}
            title={showAccuracy ? 'Hide accuracy circles' : 'Show accuracy circles'}
          />
          <div
            style={{
              position: 'fixed',
              bottom: '9vmin',
              right: '8vmin',
              borderRadius: '2vmin',
              height: '2vmin',
              width: '2vmin',
              background: showAccuracy ? '#4CAF50' : '#888',
              cursor: 'pointer',
              zIndex: 1000,
            }}
            onClick={() => setShowAccuracy(!showAccuracy)}
          />
        </div>
      )}

      {/* Per-session unlock toggle — only for a locked (non-SuperAdmin) user in timespace */}
      {userIsLocked && projection === 'timespace' && (
        <button
          style={{
            position: 'fixed',
            bottom: '20vmin',
            right: '7vmin',
            padding: '0.6em 1em',
            borderRadius: '6px',
            border: 'none',
            background: cameraLockOverride ? '#16a34a' : '#444c',
            color: '#fff',
            fontSize: 'max(12px, 1.6vmin)',
            cursor: 'pointer',
            zIndex: 1000,
          }}
          onClick={() => setCameraLockOverride((v) => !v)}
          title={
            cameraLockOverride
              ? 'Re-apply the camera lock'
              : 'Temporarily unlock the map for this session'
          }
        >
          {cameraLockOverride ? 'Re-lock map' : 'Unlock map'}
        </button>
      )}

      {/* Camera lock editor — SuperAdmin only, timespace only */}
      {isSuperAdmin && projection === 'timespace' && (
        <button
          style={{
            position: 'fixed',
            bottom: '20vmin',
            right: '7vmin',
            padding: '0.6em 1em',
            borderRadius: '6px',
            border: 'none',
            background: cameraLockEditorOpen ? '#2563eb' : '#444c',
            color: '#fff',
            fontSize: 'max(12px, 1.6vmin)',
            cursor: 'pointer',
            zIndex: 1000,
          }}
          onClick={() => {
            setCameraLockEditorOpen((open) => {
              const next = !open;
              if (!next) {
                // Closing — reset transient drawing state
                setCameraLockDrawing(false);
                setCameraLockPendingShape(null);
                setCameraLockDrawZoom(null);
              }
              return next;
            });
          }}
          title="Manage camera lock"
        >
          Camera lock
        </button>
      )}

      {cameraLockEditorOpen && (
        <CameraLockEditor
          drawing={cameraLockDrawing}
          pendingShape={cameraLockPendingShape}
          drawZoom={cameraLockDrawZoom}
          onStartDraw={() => {
            setCameraLockPendingShape(null);
            setCameraLockDrawZoom(null);
            setCameraLockDrawing(true);
          }}
          onDiscardShape={() => {
            setCameraLockPendingShape(null);
            setCameraLockDrawZoom(null);
          }}
          onClose={() => {
            setCameraLockEditorOpen(false);
            setCameraLockDrawing(false);
            setCameraLockPendingShape(null);
            setCameraLockDrawZoom(null);
          }}
        />
      )}

      {areaEditorOpen && (
        <AreaEditor
          onClose={() => {
            setAreaEditorOpen(false);
            setDrawMode(null);
            setPendingShape(null);
            setAwaitingPosition(false);
            setPendingPosition(null);
          }}
          drawMode={drawMode}
          setDrawMode={setDrawMode}
          pendingShape={pendingShape}
          pendingPosition={pendingPosition}
          onSave={handleAreaSave}
          onDiscard={() => setPendingShape(null)}
          onRequestPosition={() => setAwaitingPosition(true)}
          onCancelPosition={() => {
            setAwaitingPosition(false);
            setPendingPosition(null);
          }}
        />
      )}

      {/* Error display */}
      {error && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#fff',
            padding: '20px',
            border: '2px solid #f00',
            borderRadius: '5px',
            zIndex: 2000,
          }}
        >
          <p>Error: {error}</p>
          <button onClick={() => setError(null)}>Close</button>
        </div>
      )}
    </div>
  );
};

export default Map;
